import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { Card } from "../components/ui/index.js";
import { AppModalShell } from "../components/app-ui.js";
import { companyPageTitle } from "../utils/company.js";
import { formatCurrency } from "../utils/formatters.js";
import {
  Users,
  DollarSign,
  ShoppingCart,
  Droplets,
  Banknote,
  CookingPot,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import { getDashboardStats } from "../utils/api.js";
import { LoadingState } from "../components/motion.js";
import { resolveBankImage } from "../utils/venezuelanBanks.js";
import { Helmet } from "react-helmet";

const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9"];

const compactUsd = (value) => {
  const n = Number(value) || 0;
  if (Math.abs(n) >= 1000) {
    return `$${n.toLocaleString("es-VE", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    })}`;
  }
  return formatCurrency(n);
};

const formatLiters = (value) =>
  `${Number(value || 0).toLocaleString("es-VE", {
    maximumFractionDigits: 0,
  })} L`;

const formatKg = (value) =>
  `${Number(value || 0).toLocaleString("es-VE", {
    maximumFractionDigits: 1,
  })} kg`;

const KpiCard = ({
  label,
  value,
  hint,
  icon: Icon,
  tone = "indigo",
  onClick,
  className = "",
}) => {
  const tones = {
    indigo: { border: "border-l-indigo-500", bg: "bg-indigo-50", text: "text-indigo-600" },
    amber: { border: "border-l-amber-500", bg: "bg-amber-50", text: "text-amber-600" },
    emerald: { border: "border-l-emerald-500", bg: "bg-emerald-50", text: "text-emerald-600" },
    sky: { border: "border-l-sky-500", bg: "bg-sky-50", text: "text-sky-600" },
    rose: { border: "border-l-rose-500", bg: "bg-rose-50", text: "text-rose-600" },
    violet: { border: "border-l-violet-500", bg: "bg-violet-50", text: "text-violet-600" },
  };
  const t = tones[tone] || tones.indigo;
  const interactive = Boolean(onClick);
  const Wrapper = interactive ? "button" : "div";

  return (
    <Wrapper
      type={interactive ? "button" : undefined}
      onClick={onClick}
      className={`text-left w-full ${className}`}
    >
      <Card
        className={`p-3.5 sm:p-5 border-l-4 ${t.border} h-full ${
          interactive
            ? "cursor-pointer hover:shadow-md hover:border-violet-200 transition-shadow active:scale-[0.99]"
            : ""
        }`}
      >
        <div className="flex justify-between items-start gap-2 sm:gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-gray-500 text-[10px] sm:text-[11px] font-medium uppercase tracking-wide leading-tight">
              {label}
            </p>
            <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mt-1 truncate">
              {value}
            </h3>
            {hint ? (
              <p className="text-[11px] sm:text-xs text-gray-400 mt-1 leading-snug">{hint}</p>
            ) : null}
            {interactive ? (
              <p className="mt-1.5 inline-flex items-center gap-0.5 text-[11px] font-medium text-violet-600">
                Ver detalle <ChevronRight size={12} />
              </p>
            ) : null}
          </div>
          <div className={`${t.bg} p-2 sm:p-2.5 rounded-lg ${t.text} shrink-0`}>
            <Icon size={16} className="sm:w-[18px] sm:h-[18px]" />
          </div>
        </div>
      </Card>
    </Wrapper>
  );
};

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-3">
    <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
    {subtitle ? <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p> : null}
  </div>
);

const EmptyBlock = ({ label = "Sin datos en este período", className = "h-40 sm:h-48" }) => (
  <div className={`flex items-center justify-center text-gray-400 text-sm ${className}`}>
    {label}
  </div>
);

const DashboardModule = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cheeseOpen, setCheeseOpen] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const res = await getDashboardStats();
        setStats(res.data.data);
        setError(null);
      } catch (err) {
        setError("No se pudo cargar el dashboard.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const salesChart = useMemo(
    () =>
      (stats?.sales?.weeklySales || stats?.weeklySales || []).map((w) => ({
        name: w.label || `Sem ${w.week}`,
        short: w.label ? String(w.label).split("–")[0].trim() : `S${w.week}`,
        Total: w.total || 0,
      })),
    [stats],
  );

  const milkChart = useMemo(
    () =>
      (stats?.milk?.litersTrend || []).map((w) => ({
        name: w.label,
        short: String(w.label || "").split("–")[0].trim(),
        Litros: w.liters || 0,
      })),
    [stats],
  );

  if (loading) {
    return <LoadingState variant="dashboard" label="Cargando resumen" />;
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg p-4 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!stats) {
    return <div className="p-8 text-gray-500">No hay data para el dashboard.</div>;
  }

  const kpis = stats.kpis || {};
  const sales = stats.sales || {};
  const milk = stats.milk || {};
  const cheese = stats.cheese || {};
  const cheeseProducts = cheese.byProduct || [];
  const topProducts = sales.topProducts || stats.topSellingProducts || [];
  const milkWeekLabel = stats.periods?.milkWeek?.label || "semana actual";
  const salesWeekLabel = stats.periods?.salesWeek?.label || "semana actual";
  const paidRatio =
    milk.owed > 0 ? Math.min(100, Math.round((milk.paid / milk.owed) * 100)) : null;

  return (
    <div className="page-enter space-y-3 sm:space-y-6 p-3 sm:p-4 md:p-8 max-w-full overflow-x-hidden pb-[calc(5.5rem+env(safe-area-inset-bottom))] md:pb-8">
      <Helmet>
        <title>{companyPageTitle("Dashboard")}</title>
      </Helmet>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1.5 sm:gap-2">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Resumen del negocio</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            Ventas, leche y producción de la semana
          </p>
        </div>
        <p className="text-[10px] sm:text-[11px] text-gray-400 leading-tight">
          Leche: {milkWeekLabel}
          <span className="hidden sm:inline"> · </span>
          <span className="block sm:inline">Ventas: {salesWeekLabel}</span>
        </p>
      </div>

      {/* KPIs destacados a ancho completo; el resto en grilla */}
      <div className="reveal-host space-y-2.5 sm:space-y-4">
        <div className="grid grid-cols-1 gap-2.5 sm:gap-4">
          <KpiCard
            label="Ventas de la semana"
            value={formatCurrency(kpis.weekSales || sales.weekSales || 0)}
            hint={`${sales.weekInvoices || 0} facturas`}
            icon={ShoppingCart}
            tone="indigo"
          />
          <KpiCard
            label="Cobrado esta semana"
            value={formatCurrency(kpis.collectedThisWeek || 0)}
            hint={`${sales.collectedCount || 0} pagos`}
            icon={DollarSign}
            tone="emerald"
          />
          <KpiCard
            label="Por cobrar"
            value={formatCurrency(kpis.accountsReceivable || 0)}
            hint={`${kpis.openInvoices || 0} facturas abiertas`}
            icon={AlertCircle}
            tone="amber"
          />
          <KpiCard
            label="Pendiente a proveedores"
            value={formatCurrency(kpis.milkPending || milk.pending || 0)}
            hint={
              paidRatio != null
                ? `${paidRatio}% pagado · ${formatCurrency(milk.paid || 0)}`
                : `${milk.paymentsCount || 0} abonos`
            }
            icon={Banknote}
            tone="rose"
          />
          <KpiCard
            label="Queso producido"
            value={formatKg(cheese.weekKg || 0)}
            hint={`${cheese.weekCount || 0} und. · ${cheese.weekLots || 0} lotes`}
            icon={CookingPot}
            tone="violet"
            onClick={() => setCheeseOpen(true)}
          />
        </div>
        <div className="grid grid-cols-2 gap-2.5 sm:gap-4">
          <KpiCard
            label="Clientes activos"
            value={kpis.activeClients || 0}
            hint="Últimas 3 semanas"
            icon={Users}
            tone="sky"
          />
          <KpiCard
            label="Leche recibida"
            value={formatLiters(kpis.milkLiters || milk.liters || 0)}
            hint={`${milk.activeProviders || 0} proveedores`}
            icon={Droplets}
            tone="sky"
          />
        </div>
      </div>

      <div className="reveal-host grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        <Card className="p-3.5 sm:p-5">
          <SectionTitle
            title="Ventas semanales"
            subtitle="Últimas 6 semanas de facturación"
          />
          {salesChart.some((r) => r.Total > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={salesChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="short" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis
                  tickFormatter={(v) => compactUsd(v)}
                  tick={{ fontSize: 10, fill: "#94a3b8" }}
                  width={48}
                />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), "Ventas"]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ""}
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "12px",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="Total"
                  stroke="#6366f1"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#6366f1" }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <EmptyBlock />
          )}
        </Card>

        <Card className="p-3.5 sm:p-5">
          <SectionTitle
            title="Litros de leche"
            subtitle="Recepción semanal (jue–mié)"
          />
          {milkChart.some((r) => r.Litros > 0) ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={milkChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="short" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} width={40} />
                <Tooltip
                  formatter={(value) => [formatLiters(value), "Litros"]}
                  labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ""}
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "12px",
                  }}
                />
                <Bar dataKey="Litros" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyBlock />
          )}
        </Card>
      </div>

      <div className="reveal-host grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        <Card className="p-3.5 sm:p-5">
          <SectionTitle title="Productos más vendidos" subtitle="Mes en curso" />
          {topProducts.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                layout="vertical"
                data={[...topProducts].sort((a, b) => b.quantity - a.quantity)}
                margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={88}
                  tick={{ fontSize: 10, fill: "#64748b" }}
                />
                <Tooltip
                  formatter={(value, _n, item) => [
                    `${value} und.${
                      item?.payload?.revenue
                        ? ` · ${formatCurrency(item.payload.revenue)}`
                        : ""
                    }`,
                    "Cantidad",
                  ]}
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "12px",
                  }}
                />
                <Bar dataKey="quantity" radius={[0, 4, 4, 0]}>
                  {topProducts.map((_, index) => (
                    <Cell key={`p-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyBlock />
          )}
        </Card>

        <Card className="p-3.5 sm:p-5">
          <SectionTitle
            title="Top proveedores de leche"
            subtitle="Litros de esta semana"
          />
          {(milk.topProviders || []).length > 0 ? (
            <ul className="space-y-2 sm:space-y-2.5">
              {milk.topProviders.map((row, index) => {
                const max = milk.topProviders[0]?.liters || 1;
                const pct = Math.round((row.liters / max) * 100);
                return (
                  <li key={`${row.name}-${index}`}>
                    <div className="flex justify-between text-sm mb-1 gap-2">
                      <span className="font-medium text-gray-800 truncate">{row.name}</span>
                      <span className="text-gray-500 tabular-nums shrink-0 text-xs sm:text-sm">
                        {formatLiters(row.liters)}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-sky-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyBlock label="Sin entregas esta semana" className="h-28" />
          )}
          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-slate-50 px-2.5 py-2">
              <p className="text-gray-400">Vaca</p>
              <p className="font-semibold text-gray-800">{formatLiters(milk.litersVaca)}</p>
            </div>
            <div className="rounded-lg bg-slate-50 px-2.5 py-2">
              <p className="text-gray-400">Búfalo</p>
              <p className="font-semibold text-gray-800">{formatLiters(milk.litersBufalo)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-3.5 sm:p-5 sm:col-span-2 xl:col-span-1">
          <SectionTitle
            title="Pagos por banco"
            subtitle="Abonos de leche esta semana"
          />
          {(milk.byBank || []).length > 0 ? (
            <ul className="space-y-2 sm:space-y-2.5 max-h-56 sm:max-h-64 overflow-y-auto pr-1">
              {milk.byBank.map((row) => {
                const image = resolveBankImage({ id: row.bankId, name: row.bankName });
                return (
                  <li
                    key={row.bankId || row.bankName}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      {image ? (
                        <img
                          src={image}
                          alt=""
                          className="h-7 w-7 rounded object-contain border border-gray-100 bg-white"
                        />
                      ) : (
                        <span className="h-7 w-7 rounded bg-gray-100" />
                      )}
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-800 truncate">
                          {row.bankName}
                        </span>
                        <span className="block text-[11px] text-gray-400">
                          {row.count} pago{row.count === 1 ? "" : "s"}
                        </span>
                      </span>
                    </span>
                    <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">
                      {formatCurrency(row.amountInUSD)}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyBlock label="Sin abonos con banco esta semana" className="h-28" />
          )}
          <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
            Mes: {formatCurrency(sales.monthSales || kpis.monthSales || 0)} en ventas
          </div>
        </Card>
      </div>

      <AppModalShell
        open={cheeseOpen}
        onClose={() => setCheeseOpen(false)}
        title="Queso producido"
        fitContent
        widthClass="max-w-lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-violet-50 px-2 py-2.5">
              <p className="text-[10px] uppercase text-violet-500 font-medium">Total</p>
              <p className="text-sm font-bold text-violet-900">{formatKg(cheese.weekKg)}</p>
            </div>
            <div className="rounded-lg bg-slate-50 px-2 py-2.5">
              <p className="text-[10px] uppercase text-gray-400 font-medium">Unidades</p>
              <p className="text-sm font-bold text-gray-900">{cheese.weekCount || 0}</p>
            </div>
            <div className="rounded-lg bg-slate-50 px-2 py-2.5">
              <p className="text-[10px] uppercase text-gray-400 font-medium">Lotes</p>
              <p className="text-sm font-bold text-gray-900">{cheese.weekLots || 0}</p>
            </div>
          </div>

          <p className="text-xs text-gray-500">Semana de leche: {milkWeekLabel}</p>

          {cheeseProducts.length > 0 ? (
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
              {cheeseProducts.map((row) => (
                <li
                  key={row.productId || row.name}
                  className="flex items-center justify-between gap-3 px-3.5 py-3 bg-white"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{row.name}</p>
                    <p className="text-[11px] text-gray-400">
                      {row.lots || 0} lote{(row.lots || 0) === 1 ? "" : "s"}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-violet-700 tabular-nums">
                      {formatKg(row.kg)}
                    </p>
                    <p className="text-[11px] text-gray-500 tabular-nums">
                      {Number(row.cheeseCount || 0).toLocaleString("es-VE")} und.
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-400 text-center py-8">
              No hay producción de queso esta semana.
            </p>
          )}
        </div>
      </AppModalShell>
    </div>
  );
};

export default DashboardModule;
